import { LOCAL_STORAGE_KEYS, CAPTCHA_SITEKEY } from './constants';
import { UserInfo } from '../modules/auth';
import { PortfolioPayoffCurveObj } from '@app/_shared';
import { AES, enc } from 'crypto-js';

class StorageHelperClass {

    constructor() { }

    private get(key: string, decrypt = false) {
        if (localStorage.getItem(key)) {
            if (decrypt) {
                const decryptData = AES.decrypt(localStorage.getItem(key), CAPTCHA_SITEKEY).toString(enc.Utf8);
                return JSON.parse(decryptData);
            }
            return JSON.parse(localStorage.getItem(key));
        }
    }

    private set(key: string, value: any, encrypt = false) {
        value = JSON.stringify(value)
        if (encrypt) {
            value = AES.encrypt(value, CAPTCHA_SITEKEY).toString()
        }
        localStorage.setItem(key, value);
    }

    hasKey(key: string): boolean {
        return localStorage.hasOwnProperty(key);
    }

    clear() {
        localStorage.removeItem(LOCAL_STORAGE_KEYS.USER_INFO);
    }
    clearByKey(key) {
        localStorage.removeItem(key);
    }

    clearAll() {
        localStorage.clear();
    }

    // Authenticated user's info
    get userInfo(): UserInfo {
        return this.get(LOCAL_STORAGE_KEYS.USER_INFO, true);
    }

    set userInfo(userInfo: UserInfo) {
        this.set(LOCAL_STORAGE_KEYS.USER_INFO, userInfo, true);
    }

    // 2FA token in case of MPIN login.
    get TwoFAToken() {
        return this.get(LOCAL_STORAGE_KEYS.TWO_FA_TOKEN);
    }

    set TwoFAToken(token: string) {
        this.set(LOCAL_STORAGE_KEYS.TWO_FA_TOKEN, token);
    }

    get lastClickedSymbolID() {
        return this.get(LOCAL_STORAGE_KEYS.LAST_OPENED_SYMBOL_ID);
    }

    set lastClickedSymbolID(symbolMap: { [key: number]: number }) {
        this.set(LOCAL_STORAGE_KEYS.LAST_OPENED_SYMBOL_ID, symbolMap);
    }

    set Uuid(uuid: string) {
        this.set(LOCAL_STORAGE_KEYS.UUID, uuid, true);
    }

    get Uuid() {
        return this.get(LOCAL_STORAGE_KEYS.UUID, true);
    }

    set activeChartType(type) {
        this.set(LOCAL_STORAGE_KEYS.ACTIVE_CHART_TYPE, type);
    }

    get activeChartType() {
        return this.get(LOCAL_STORAGE_KEYS.ACTIVE_CHART_TYPE);
    }

    set completeChartType(type) {
        this.set(LOCAL_STORAGE_KEYS.COMPLETE_CHART_TYPE, type);
    }

    get completeChartType() {
        return this.get(LOCAL_STORAGE_KEYS.COMPLETE_CHART_TYPE);
    }

    set alogLevel(map) {
        this.set(LOCAL_STORAGE_KEYS.ALGO_LEVEL, map);
    }

    get alogLevel() {
        return this.get(LOCAL_STORAGE_KEYS.ALGO_LEVEL);
    }

    set portLevel(map) {
        this.set(LOCAL_STORAGE_KEYS.PORTFOLIO_LEVEL, map);
    }

    get portLevel() {
        return this.get(LOCAL_STORAGE_KEYS.PORTFOLIO_LEVEL);
    }

    set symbolLevel(map) {
        this.set(LOCAL_STORAGE_KEYS.SYMBOL_LEVEL, map);
    }

    get symbolLevel() {
        return this.get(LOCAL_STORAGE_KEYS.SYMBOL_LEVEL);
    }

    set keyWords(map) {
        this.set(LOCAL_STORAGE_KEYS.KEYWORDS, map);
    }

    get keyWords() {
        return this.get(LOCAL_STORAGE_KEYS.KEYWORDS);
    }

    set currentStrategyTab(map) {
        this.set(LOCAL_STORAGE_KEYS.CURRENT_TAB, map);
    }

    get currentStrategyTab() {
        return this.get(LOCAL_STORAGE_KEYS.CURRENT_TAB);
    }

    set payOffData(data: PortfolioPayoffCurveObj) {
        this.set(LOCAL_STORAGE_KEYS.PAYOFF_DATA, data);
    }

    get payOffData(): PortfolioPayoffCurveObj {
        return this.get(LOCAL_STORAGE_KEYS.PAYOFF_DATA);
    }

    set pricingShown(flag: boolean) {
        this.set(LOCAL_STORAGE_KEYS.IS_PRICING_SHOWN, flag);
    }

    get pricingShown(): boolean {
        return this.get(LOCAL_STORAGE_KEYS.IS_PRICING_SHOWN);
    }

    set selectedPayOffTabIndex(index: number) {
        this.set(LOCAL_STORAGE_KEYS.SELECTED_PAY_OFF_INDEX, index);
    }

    get selectedPayOffTabIndex(): number {
        return this.get(LOCAL_STORAGE_KEYS.SELECTED_PAY_OFF_INDEX);
    }

    set showPinnedSymbols(value: boolean) {
        this.set(LOCAL_STORAGE_KEYS.SHOW_PINNED_SYMBOLS, value);
    }

    get showPinnedSymbols(): boolean {
        return this.get(LOCAL_STORAGE_KEYS.SHOW_PINNED_SYMBOLS);
    }

    set deltaValue(value: number) {
        this.set(LOCAL_STORAGE_KEYS.DELTA_VALUE, value);
    }

    get deltaValue(): number {
        return this.get(LOCAL_STORAGE_KEYS.DELTA_VALUE);
    }

    get popupAccepted() {
        return this.get(LOCAL_STORAGE_KEYS.POPUP_ACCEPTED);
    }

    set popupAccepted(value: boolean) {
        this.set(LOCAL_STORAGE_KEYS.POPUP_ACCEPTED, value);
    }

    get freePlanExtendedPopupAccepted() {
        return this.get(LOCAL_STORAGE_KEYS.FREE_PLAN_EXTENDED_POPUP_ACCEPTED);
    }

    set freePlanExtendedPopupAccepted(value: boolean) {
        this.set(LOCAL_STORAGE_KEYS.FREE_PLAN_EXTENDED_POPUP_ACCEPTED, value);
    }

    get emailCommunication() {
        return this.get(LOCAL_STORAGE_KEYS.EMAIL_COMMUNICATION);
    }

    set emailCommunication(value: boolean) {
        this.set(LOCAL_STORAGE_KEYS.EMAIL_COMMUNICATION, value);
    }

    get emailCommunicationId() {
        return this.get(LOCAL_STORAGE_KEYS.EMAIL_COMMUNICATION_ID);
    }

    set emailCommunicationId(value: string) {
        this.set(LOCAL_STORAGE_KEYS.EMAIL_COMMUNICATION_ID, value);
    }

    get backtestViewStatus() {
        return this.get(LOCAL_STORAGE_KEYS.BACKTEST_VIEW_STATUS);
    }

    set backtestViewStatus(data) {
        this.set(LOCAL_STORAGE_KEYS.BACKTEST_VIEW_STATUS, data);
    }

    get postSiRedirectUrl() {
        return this.get(LOCAL_STORAGE_KEYS.POST_SI_REDIRECT_URL);
    }

    set postSiRedirectUrl(data) {
        this.set(LOCAL_STORAGE_KEYS.POST_SI_REDIRECT_URL, data);
    }

    set MasterTrustAccessToken(data) {
        this.set(LOCAL_STORAGE_KEYS.MASTERTRUST_ACCESS_TOKEN, data);
    }

    get MasterTrustAccessToken() {
        return this.get(LOCAL_STORAGE_KEYS.MASTERTRUST_ACCESS_TOKEN);
    }

    set isNewFeatureAlertClosed(value) {
        this.set(LOCAL_STORAGE_KEYS.IS_NEW_FEATURE_ALERT_CLOSED, value);
    }

    get isNewFeatureAlertClosed() {
        return this.get(LOCAL_STORAGE_KEYS.IS_NEW_FEATURE_ALERT_CLOSED);
    }

    set aiDisclaimerAccepted(value: boolean) {
        this.set(LOCAL_STORAGE_KEYS.AI_DISCLAIMER_ACCEPTED, value);
    }

    get aiDisclaimerAccepted() {
        return this.get(LOCAL_STORAGE_KEYS.AI_DISCLAIMER_ACCEPTED);
    }

    set advisorThreadId(value: string) {
        this.set(LOCAL_STORAGE_KEYS.ADVISOR_THREAD_ID, value);
    }

    get advisorThreadId() {
        return this.get(LOCAL_STORAGE_KEYS.ADVISOR_THREAD_ID);
    }

    get showSharedSection() {
        return this.get(LOCAL_STORAGE_KEYS.SHOW_SHARED_SECTION);
    }

    set showSharedSection(boolean) {
        this.set(LOCAL_STORAGE_KEYS.SHOW_SHARED_SECTION,boolean);
    }

    get isFirstLogin() {
        return this.get(LOCAL_STORAGE_KEYS.IS_FIRST_LOGIN);
    }

    set isFirstLogin(value: boolean) {
        this.set(LOCAL_STORAGE_KEYS.IS_FIRST_LOGIN, value);
    }

    get operatorToken() {
        return this.get(LOCAL_STORAGE_KEYS.OPERATOR_TOKEN);
    }

    set operatorToken(value: string) {
        this.set(LOCAL_STORAGE_KEYS.OPERATOR_TOKEN, value);
    }

    get operatorAdminToken() {
        return this.get(LOCAL_STORAGE_KEYS.OPERATOR_ADMIN_TOKEN);
    }

    set operatorAdminToken(value: string) {
        this.set(LOCAL_STORAGE_KEYS.OPERATOR_ADMIN_TOKEN, value);
    }

    set bodMargin(value: number) {
        this.set(LOCAL_STORAGE_KEYS.BOD_MARGIN, value);
    }

    get bodMargin() {
        return this.get(LOCAL_STORAGE_KEYS.BOD_MARGIN);
    }
    
    get lastClickedSymbol() {
        return this.get(LOCAL_STORAGE_KEYS.LAST_CLICKED_SYMBOL);
    }

    set lastClickedSymbol(value: any) {
        this.set(LOCAL_STORAGE_KEYS.LAST_CLICKED_SYMBOL, value);
    }

    set selectedMarketWatch(value: any) {
        this.set(LOCAL_STORAGE_KEYS.SELECTED_MARKET_WATCH, value);
    }

    get selectedMarketWatch() {
        return this.get(LOCAL_STORAGE_KEYS.SELECTED_MARKET_WATCH);
    }

    get defaultMarketWatch() {
        return this.get(LOCAL_STORAGE_KEYS.DEFAULT_MARKET_WATCH);
    }

    set defaultMarketWatch(value: any) {
        this.set(LOCAL_STORAGE_KEYS.DEFAULT_MARKET_WATCH, value);
    }

    get tradingViewInterval() {
        return this.get(LOCAL_STORAGE_KEYS.TRADING_VIEW_INTERVAL);
    }

    set tradingViewInterval(value: string) {
        this.set(LOCAL_STORAGE_KEYS.TRADING_VIEW_INTERVAL, value);
    }

    get lastLoggedInUser() {
        return this.get(LOCAL_STORAGE_KEYS.LAST_LOGGED_IN_USER);
    }

    set lastLoggedInUser(value: any) {
        this.set(LOCAL_STORAGE_KEYS.LAST_LOGGED_IN_USER, value);
    }

    get manageBrokersSelectedTab() {
        return this.get(LOCAL_STORAGE_KEYS.MANAGE_BROKERS_SELECTED_TAB);
    }

    set manageBrokersSelectedTab(value: number) {
        this.set(LOCAL_STORAGE_KEYS.MANAGE_BROKERS_SELECTED_TAB, value);
    }
    
    get isFixedAiOpened() {
        return this.get(LOCAL_STORAGE_KEYS.IS_FIXED_AI_OPENED);
    }

    set isFixedAiOpened(value: boolean) {
        this.set(LOCAL_STORAGE_KEYS.IS_FIXED_AI_OPENED, value);
    }
    
    get fromMobile() {
        return this.get(LOCAL_STORAGE_KEYS.FROM_MOBILE);
    }

    set fromMobile(value: boolean) {
        this.set(LOCAL_STORAGE_KEYS.FROM_MOBILE, value);
    }

    get isFromDeployedPage() {
        return this.get(LOCAL_STORAGE_KEYS.IS_FROM_DEPLOYED_PAGE);
    }

    set isFromDeployedPage(object: any) {
        this.set(LOCAL_STORAGE_KEYS.IS_FROM_DEPLOYED_PAGE, object);
    }
}

export const StorageHelper = new StorageHelperClass();
